import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock } from 'lucide-react';
import apiService from '../../services/apiService';
import { STATUS_LABELS, STATUS_TONES } from './forecastUsers';

const SLA_TONE = { breach: 'red', watch: 'yellow', healthy: 'green' };

/**
 * B1 — "waiting on me" review queue. Server-computed SLA aging and counts;
 * never derive totals from the visible rows.
 */
const ForecastWaitingQueue = ({ cycleCode, actor }) => {
  const [queue, setQueue] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!actor) return undefined;
    apiService
      .fetchForecastWaitingQueue({ cycleCode, actor })
      .then((result) => { if (!cancelled) setQueue(result); })
      .catch((err) => { if (!cancelled) setError(err?.response?.data?.error || err.message); });
    return () => { cancelled = true; };
  }, [cycleCode, actor]);

  if (error) return null; // queue is a supplement — fail quietly, console still works
  if (!queue) return null;

  const items = queue.items || [];
  const counts = queue.counts || {};

  return (
    <section className="forecast-panel forecast-waiting-queue">
      <div className="forecast-panel-head">
        <span className="forecast-section-code">RV_0Q ● Waiting on me</span>
        <h2>
          <Clock size={16} style={{ marginRight: 6, verticalAlign: '-2px' }} />
          Packages awaiting your review
        </h2>
        <span className="forecast-submit-chips" style={{ marginLeft: 'auto' }}>
          <span className="forecast-health-chip neutral">{counts.total ?? items.length} total</span>
          {counts.breached > 0 && <span className="forecast-health-chip red">{counts.breached} breached</span>}
          {counts.watch > 0 && <span className="forecast-health-chip yellow">{counts.watch} watch</span>}
        </span>
      </div>

      {items.length === 0 ? (
        <p className="forecast-submit-note">Nothing is waiting on you right now.</p>
      ) : (
        <div className="forecast-home-table-wrap">
          <table className="forecast-home-table">
            <thead>
              <tr>
                <th>Unit</th>
                <th>Status</th>
                <th>Age (bus. days)</th>
                <th>SLA due</th>
                <th>SLA</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.submissionId}>
                  <td>
                    <strong>{item.division}</strong>
                    <span className="forecast-home-market"> · {item.market}</span>
                    {item.returnedComment && (
                      <div className="forecast-queue-returned">↩ {item.returnedComment}</div>
                    )}
                  </td>
                  <td>
                    <span className={`forecast-status-chip ${STATUS_TONES[item.status] || 'neutral'}`}>
                      {STATUS_LABELS[item.status] || item.status}
                    </span>
                  </td>
                  <td className="forecast-mono">
                    {item.ageBusinessDays ?? '—'}
                    {item.slaDays ? <span className="forecast-home-market"> / {item.slaDays}</span> : null}
                  </td>
                  <td className="forecast-mono">{item.slaDueAt ? String(item.slaDueAt).slice(0, 10) : '—'}</td>
                  <td>
                    <span className={`forecast-health-chip ${SLA_TONE[item.slaState] || 'neutral'}`}>
                      {item.slaState || '—'}
                    </span>
                  </td>
                  <td>
                    <Link to={item.href || `/forecasting/review/${item.submissionId}`} className="forecast-home-action">
                      Review
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default ForecastWaitingQueue;
